import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PlansList from '../components/PlansList';
import PoliciesList from '../components/PoliciesList';
import CompaniesClientsList from '../components/CompaniesClientsList';
import SummaryCompanies from '../components/SummaryCompanies';
import CompanyMovements from '../components/CompanyMovements';
import AdminPreClientsImport from '../components/AdminPreClientsImport';
import InactiveClientsList from '../components/InactiveClientsList';
import Footer from '../components/Footer';
import EditCompanyModal from '../components/EditCompanyModal';
import AddLojistaModal from '../components/AddLojistaModal';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const USER_IMAGE_DEFAULT = process.env.REACT_APP_USER_IMAGE_DEFAULT;

function maskCelular(value) {
  if (!value) return '';
  return value
    .replace(/\D/g, '')
    .replace(/^(\d{2})(\d)/g, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
}

function AdmDashboard() {
  const [lojistas, setLojistas] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('lojistas');
  const [addLojistaModalVisible, setAddLojistaModalVisible] = useState(false);
  const [editCompanyModalVisible, setEditCompanyModalVisible] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const navigate = useNavigate();

  // Converte a URL do Google Drive para um link de imagem direto usando um proxy
  const getGoogleDriveImageProxyUrl = (url) => {
    if (typeof url !== 'string' || !url.includes('drive.google.com')) {
      return url; // Retorna a URL original se não for do Google Drive
    }
  
    // Extrai o ID do arquivo da URL
    const match = url.match(/file\/d\/([^/]+)/);
    if (match && match[1]) {
      const fileId = match[1];
      // Usa um proxy para forçar o download direto da imagem
      return `https://images.weserv.nl/?url=https://drive.google.com/uc?id=${fileId}`;
    }
  
    return url; // Retorna a URL original se não conseguir extrair o ID
  };
  const token = localStorage.getItem('token');
  const fetchLojistas = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/lojistas`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      //alert('status '+data.status);
      if (response.ok && Array.isArray(data)) {
        setLojistas(data);
      } else {
        setLojistas([]);
      }
    } catch (error) {
      setLojistas([]);
    }
  }, [token]);

  useEffect(() => {
    fetchLojistas();
  }, [fetchLojistas]);

  const handleLogoff = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleAddLojista = () => {
    setAddLojistaModalVisible(true);
  };

  const handleAddLojistaModalClose = () => {
    setAddLojistaModalVisible(false);
  };

  const handleLojistaAdded = () => {
    fetchLojistas();
    setAddLojistaModalVisible(false);
  };

  const handleEditCompany = (lojista) => {
    let companyData = { ...lojista };

    if (lojista.companyId) {
      // Garante que o ID da empresa seja passado como 'id', que é o esperado pelo modal de edição.
      companyData.id = lojista.companyId;
      localStorage.setItem('company_id', lojista.companyId);
    }

    setCompanyToEdit(companyData);
    setEditCompanyModalVisible(true);
  };

  const handleEditCompanyModalClose = () => {
    setEditCompanyModalVisible(false);
    setCompanyToEdit(null);
  };

  const handleCompanyUpdated = () => {
    fetchLojistas();
    handleEditCompanyModalClose();
  };

  const renderContent = () => {
    switch (selectedMenu) {
      case 'lojistas':
        return (
          <section className="dashboard-section">
            <h3>Lojistas</h3>
            {lojistas.length > 0 ? (
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Nome Fantasia</th>
                    <th>E-mail</th>
                    <th>Nome do Responsável</th>
                    <th>Telefone</th>
                    <th>Logo</th>
                    <th>Valor do Ponto</th>
                    <th>Dias de Ausência</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lojistas.map((lojista, index) => (
                    <tr key={lojista.id || index}>
                      <td>{lojista.nomeFantasia}</td>
                      <td>{lojista.email}</td>
                      <td>{lojista.nomeResponsavel}</td>
                      <td>{maskCelular(lojista.telefone)}</td>
                      <td>
                        {lojista.urlLogo ? (
                          <img
                            src={getGoogleDriveImageProxyUrl(lojista.urlLogo)}
                            alt={`${lojista.nomeFantasia} Logo`}
                            style={{ width: '50px', height: '50px' }}
                            onError={(e) => { e.target.onerror = null; e.target.src = USER_IMAGE_DEFAULT; }}
                          />
                        ) : <img src={USER_IMAGE_DEFAULT} alt="Default" style={{ width: '50px', height: '50px' }} />}
                      </td>
                      <td>{lojista.valor_ponto}</td>
                      <td>{lojista.dias_ausencia}</td>
                      <td>
                        <button onClick={() => handleEditCompany(lojista)} title="Editar" className="action-button">
                          ✎
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Nenhum lojista encontrado.</p>
            )}
          </section>
        );
      case 'planos':
        return <PlansList />;
      case 'politicas':
        return <PoliciesList />;
      case 'companiesClients':
        return <CompaniesClientsList />;
      case 'summaryCompanies':
        return <SummaryCompanies lojistas={lojistas} />;
      case 'companyMovements':
        return <CompanyMovements lojistas={lojistas} />;
      case 'importacaoContatos':
        return <AdminPreClientsImport />;
      case 'inactiveClients':
        return <InactiveClientsList />;
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h2>Painel do Administrador</h2>
        <button onClick={handleLogoff} className="button-style logoff-button">
          Logoff
        </button>
      </header>

      <nav
        className="hamburger-menu"
        onMouseEnter={() => setMenuOpen(true)}
        onMouseLeave={() => setMenuOpen(false)}
      >
        <button className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
          ☰
        </button>
        {menuOpen && (
          <div className="menu-options">            
            <button onClick={() => setSelectedMenu('lojistas')} className="menu-option">
              Lojistas
            </button>
            <button onClick={handleAddLojista} className="menu-option">
              Adicionar Lojista
            </button>
            <button onClick={() => setSelectedMenu('companiesClients')} className="menu-option">
              Relação Lojistas X Clientes
            </button>
            <button onClick={() => setSelectedMenu('summaryCompanies')} className="menu-option">
              Fluxo de Lojistas
            </button>
            <button onClick={() => setSelectedMenu('companyMovements')} className="menu-option">
              Movimentação dos Lojistas
            </button>
            <button onClick={() => setSelectedMenu('importacaoContatos')} className="menu-option">
              Importar contatos
            </button>
            <button onClick={() => setSelectedMenu('inactiveClients')} className="menu-option">
              Clientes inativos
            </button>
            <button onClick={() => setSelectedMenu('planos')} className="menu-option">
              Planos
            </button>
            <button onClick={() => setSelectedMenu('politicas')} className="menu-option">
              Políticas
            </button>
          </div>
        )}
      </nav>

      {renderContent()}

      {addLojistaModalVisible && (
        <AddLojistaModal
          isOpen={addLojistaModalVisible}
          onClose={handleAddLojistaModalClose}
          onLojistaAdded={handleLojistaAdded}
        />
      )}

      {editCompanyModalVisible && (
        <EditCompanyModal
          isOpen={editCompanyModalVisible}
          company={companyToEdit}
          onClose={handleEditCompanyModalClose}
          onCompanyUpdated={handleCompanyUpdated}
        />
      )}
      <Footer />
    </div>
  );
}

export default AdmDashboard;
